import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FaUser, FaLock, FaArrowRight, FaExclamationTriangle } from 'react-icons/fa';
import { useAuth } from '../contexts/AuthContext';
import logoImg from '../assets/logo-godaiStudios.png';

const Container = styled.div`
  display: flex;
  height: 100vh;
  overflow: hidden;
`;

const LeftPanel = styled.div`
  flex: 1;
  background: linear-gradient(135deg, #222, #444);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  color: white;
  padding: 40px;
  
  @media (max-width: 768px) {
    display: none;
  }
`;

const LogoImage = styled.img`
  height: 70px;
  width: auto;
  margin-bottom: 20px;
`;

const WelcomeText = styled.h1`
  font-size: 36px;
  margin-bottom: 20px;
  text-align: center;
`;

const Description = styled.p`
  font-size: 16px;
  max-width: 400px;
  text-align: center;
  line-height: 1.6;
  margin-bottom: 40px;
  opacity: 0.8;
`;

const RightPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: #f5f5f5;
  padding: 40px;
`;

const LoginForm = styled.form`
  width: 100%;
  max-width: 400px;
  background: white;
  border-radius: 10px;
  padding: 40px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
`;

const FormTitle = styled.h2`
  font-size: 24px;
  margin-bottom: 30px;
  color: #333;
  text-align: center;
`;

const InputGroup = styled.div`
  position: relative;
  margin-bottom: 25px;
`;

const Input = styled.input`
  width: 100%;
  padding: 15px 15px 15px 50px;
  border: 1px solid ${props => props.error ? '#e74c3c' : '#eee'};
  border-radius: 6px;
  font-size: 14px;
  transition: all 0.3s;
  
  &:focus {
    border-color: #333;
    box-shadow: 0 0 0 2px rgba(0, 0, 0, 0.1);
    outline: none;
  }
`;

const InputIcon = styled.div`
  position: absolute;
  left: 20px;
  top: 50%;
  transform: translateY(-50%);
  color: #999;
`;

const Button = styled.button`
  width: 100%;
  padding: 15px;
  background-color: #222;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.3s;
  display: flex;
  justify-content: center;
  align-items: center;
  
  &:hover {
    background-color: #000;
  }
  
  &:disabled {
    background-color: #999;
    cursor: not-allowed;
  }
  
  svg {
    margin-left: 8px;
  }
`;

const ErrorMessage = styled.div`
  color: #e74c3c;
  font-size: 14px;
  margin-bottom: 20px;
  text-align: center;
`;

const ForgotPassword = styled.div`
  text-align: center;
  margin-top: 20px;
  
  a {
    color: #666;
    font-size: 14px;
    text-decoration: none;
    
    &:hover {
      color: #333;
      text-decoration: underline;
    }
  }
`;

const CreateAccount = styled.div`
  text-align: center;
  margin-top: 30px;
  font-size: 14px;
  color: #666;
  
  a {
    color: #222;
    font-weight: 500;
    margin-left: 5px;
    text-decoration: none;
    
    &:hover {
      text-decoration: underline;
    }
  }
`;

const AccessDeniedMessage = styled.div`
  margin-bottom: 20px;
  padding: 15px;
  background-color: #fff3f3;
  border-left: 4px solid #e74c3c;
  color: #333;
  font-size: 14px;
  line-height: 1.5;
  display: flex;
  align-items: center;
  
  svg {
    color: #e74c3c;
    margin-right: 10px;
    font-size: 18px;
    flex-shrink: 0;
  }
`;

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState('');
  const [loading, setLoading] = useState(false);
  const [accessDenied, setAccessDenied] = useState(false);
  
  const navigate = useNavigate();
  const location = useLocation();
  const { signIn, error, clearError, isAuthenticated } = useAuth();
  
  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);
  
  // Check if redirected due to access denied
  useEffect(() => {
    if (location.state?.accessDenied) {
      setAccessDenied(true);
      setFormError('Access denied. Your account does not have admin privileges.');
    }
  }, [location.state]);
  
  // Update form error if auth context has an error
  useEffect(() => {
    if (error) {
      setFormError(error);
      if (error.includes('Admin privileges required')) {
        setAccessDenied(true);
      }
    }
  }, [error]);
  
  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      setFormError('Please enter both email and password');
      return;
    }
    
    setLoading(true);
    setFormError('');
    setAccessDenied(false);
    clearError();
    
    try {
      await signIn(email, password);
      // No need to navigate, the useEffect will handle that
    } catch (err) {
      console.error('Login error:', err);
      
      // Display specific form errors
      if (err.message.includes('Admin privileges required')) {
        setAccessDenied(true);
      } else {
        switch (err.code) {
          case 'auth/invalid-email':
            setFormError('Invalid email format');
            break;
          case 'auth/user-not-found':
          case 'auth/wrong-password':
            setFormError('Invalid email or password');
            break;
          case 'auth/too-many-requests':
            setFormError('Too many failed login attempts. Please try again later');
            break;
          default:
            if (!formError) {
              setFormError('Failed to log in. Please try again');
            }
        }
      }
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <Container>
      <LeftPanel>
        <LogoImage src={logoImg} alt="Godai Studios Logo" />
        <WelcomeText>Admin Portal</WelcomeText>
        <Description>
          Manage products, workshops, orders, and more from this central administration dashboard.
        </Description>
      </LeftPanel>
      <RightPanel>
        <LoginForm onSubmit={handleLogin}>
          <FormTitle>Admin Sign In</FormTitle>
          
          {accessDenied && (
            <AccessDeniedMessage>
              <FaExclamationTriangle />
              <div>
                Access denied. This portal is restricted to administrators only. 
                If you believe you should have access, please contact the system administrator.
              </div>
            </AccessDeniedMessage>
          )}
          
          {formError && !accessDenied && <ErrorMessage>{formError}</ErrorMessage>}
          
          <InputGroup>
            <InputIcon>
              <FaUser />
            </InputIcon>
            <Input 
              type="email" 
              placeholder="Email Address" 
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={formError.includes('email')}
              autoComplete="email"
            />
          </InputGroup>
          
          <InputGroup>
            <InputIcon>
              <FaLock />
            </InputIcon>
            <Input 
              type="password" 
              placeholder="Password" 
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              error={formError.includes('password')}
              autoComplete="current-password"
            />
          </InputGroup>
          
          <Button type="submit" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'} {!loading && <FaArrowRight />}
          </Button>
          
          <ForgotPassword>
            <Link to="/reset-password">Forgot password?</Link>
          </ForgotPassword>
          
          <CreateAccount>
            Need admin access?
            <Link to="/register">Request Account</Link>
          </CreateAccount>
        </LoginForm>
      </RightPanel>
    </Container>
  );
};

export default Login; 